// Comment-preserving edits to `cloudgrid.yaml` for the scaffold commands.
//
// `yaml`'s `parseDocument` round-trips comments + whitespace, so we use it
// instead of `parse` (which returns a plain JS object). Insertion is
// alphabetical to keep diffs predictable; renaming preserves position.

import { readFileSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import {
  Document,
  isMap,
  isPair,
  parseDocument,
  YAMLMap,
  type Node,
  type Pair,
} from 'yaml';
import { CloudGridError } from '../../utils.js';
import type { ServiceType } from './templates.js';

export interface AddServiceOpts {
  type: ServiceType;
  /** Optional `lang: typescript` for `node` services. */
  lang?: 'typescript';
  /** Optional cron schedule — required for `type: cron`. */
  schedule?: string;
  /**
   * Optional `path:` declaration. When set, the service is the entry point
   * for that prefix in the rendered Ingress. `cloudgrid init` (per backlog
   * 088) seeds the first scaffolded service with `path: /` so the yaml
   * passes backlog 082's routability validation by default.
   */
  path?: string;
}

function loadYamlDoc(cwd: string): { doc: Document; path: string } {
  const path = join(cwd, 'cloudgrid.yaml');
  let raw: string;
  try {
    raw = readFileSync(path, 'utf-8');
  } catch (err) {
    throw new CloudGridError(`Could not read cloudgrid.yaml: ${(err as Error).message}`);
  }
  const doc = parseDocument(raw);
  if (doc.errors.length > 0) {
    throw new CloudGridError(
      `cloudgrid.yaml has parse errors: ${doc.errors.map((e) => e.message).join('; ')}`,
    );
  }
  return { doc, path };
}

function getServicesMap(doc: Document, opts: { create: boolean }): YAMLMap | null {
  const root = doc.contents;
  if (!isMap(root)) {
    if (!opts.create) return null;
    throw new CloudGridError('cloudgrid.yaml must be a YAML mapping at the root.');
  }
  let services = root.get('services', true) as Node | undefined;
  if (!services && opts.create) {
    services = new YAMLMap();
    root.set('services', services);
    return services as YAMLMap;
  }
  if (services && !isMap(services)) {
    throw new CloudGridError("cloudgrid.yaml's services: block is not a mapping.");
  }
  return (services as YAMLMap | undefined) ?? null;
}

/** Names of every declared service, alphabetical, suitable for diff stability. */
export function listServiceNames(cwd: string): string[] {
  const { doc } = loadYamlDoc(cwd);
  const services = getServicesMap(doc, { create: false });
  if (!services) return [];
  const names: string[] = [];
  for (const item of services.items as Pair[]) {
    if (item.key && (item.key as { value?: unknown }).value) {
      names.push(String((item.key as { value: unknown }).value));
    }
  }
  return names;
}

/**
 * Insert a new service block alphabetically. Throws if the name already
 * exists. Preserves all existing comments.
 */
export function addService(cwd: string, name: string, opts: AddServiceOpts): void {
  const { doc, path } = loadYamlDoc(cwd);
  const services = getServicesMap(doc, { create: true });
  if (!services) throw new CloudGridError('Failed to locate or create services: block.');

  const existing = (services.items as Pair[]).find(
    (p) => (p.key as { value?: unknown })?.value === name,
  );
  if (existing) {
    throw new CloudGridError(
      `Service '${name}' already exists. Use 'cloudgrid scaffold rename' to rename, or remove first.`,
    );
  }

  const block = new YAMLMap();
  block.set('type', opts.type);
  if (opts.lang) block.set('lang', opts.lang);
  if (opts.schedule) block.set('schedule', opts.schedule);
  if (opts.path) block.set('path', opts.path);

  // Find the insertion index that keeps services in alphabetical order.
  const items = services.items as Pair[];
  let insertIdx = items.length;
  for (let i = 0; i < items.length; i++) {
    const k = String((items[i].key as { value?: unknown })?.value ?? '');
    if (name < k) {
      insertIdx = i;
      break;
    }
  }
  const newPair = doc.createPair(name, block) as Pair;
  items.splice(insertIdx, 0, newPair);

  // Promote `services: {}` (empty flow) to block style when adding the
  // first entry. `cloudgrid init` writes the empty form as flow per spec;
  // without this nudge the post-scaffold yaml would end up as
  // `services: { web: { type: node } }` and stay in flow even as more
  // services were added. Block style is the convention every example in
  // the strategy docs uses.
  if (
    items.length === 1 &&
    (services as YAMLMap & { flow?: boolean }).flow === true
  ) {
    (services as YAMLMap & { flow: boolean }).flow = false;
  }

  writeFileSync(path, String(doc));
}

/**
 * Set top-level `expose: false` on the yaml. Used by `cloudgrid init
 * --internal` per backlog 088 to opt the entity out of routability checks
 * (decision 020). Replaces the legacy `network.public: false` shape per
 * backlog 191 — `expose:` is the canonical field; `network.public:` still
 * parses with a deprecation warning but new yamls emit the new shape.
 *
 * Idempotent: running twice produces the same yaml.
 */
export function setExposeFalse(cwd: string): void {
  const { doc, path } = loadYamlDoc(cwd);
  const root = doc.contents;
  if (!isMap(root)) {
    throw new CloudGridError('cloudgrid.yaml must be a YAML mapping at the root.');
  }
  root.set('expose', false);
  writeFileSync(path, String(doc));
}

/**
 * Rename a service key in place. Position preserved. Throws if `oldName`
 * does not exist or `newName` already exists.
 */
export function renameService(cwd: string, oldName: string, newName: string): void {
  if (oldName === newName) return;
  const { doc, path } = loadYamlDoc(cwd);
  const services = getServicesMap(doc, { create: false });
  if (!services) {
    throw new CloudGridError(`Service '${oldName}' is not declared in cloudgrid.yaml.`);
  }
  const items = services.items as Pair[];
  const idx = items.findIndex((p) => (p.key as { value?: unknown })?.value === oldName);
  if (idx === -1) {
    throw new CloudGridError(`Service '${oldName}' is not declared in cloudgrid.yaml.`);
  }
  const collidesIdx = items.findIndex((p) => (p.key as { value?: unknown })?.value === newName);
  if (collidesIdx !== -1) {
    throw new CloudGridError(
      `Service '${newName}' already exists in cloudgrid.yaml.`,
    );
  }
  // Mutate the key scalar's value rather than replacing the pair so any
  // attached comments / formatting stay attached.
  const pair = items[idx];
  if (isPair(pair) && pair.key && typeof (pair.key as { value?: unknown }).value === 'string') {
    (pair.key as { value: string }).value = newName;
  } else {
    // Fall back to a fresh pair if the existing key node isn't a scalar.
    const block = pair.value as Node;
    items[idx] = doc.createPair(newName, block);
  }
  writeFileSync(path, String(doc));
}
